import { useSelector } from 'react-redux'
import { selectCustomerProfile, selectHomebotNetworkEnabled } from '../../store/selectors/customerProfile'
import {
  selectPreferredLoanOfficerMembership,
  selectPreferredRealEstateAgentMembership,
  selectPreferredInsuranceAgentMembership
} from '../../store/selectors/clientTeamMemberships'
import { PartnerType } from '../../Components/shared/VerificationFlow'
import { CustomerProfileData } from '../../types/customerProfile'
import { ClientTeamMembershipCustomerProfileData } from '../../types/clientTeamMembership'

// Common branding properties that are accessed from both CustomerProfileData and slimmed down ClientTeamMembershipCustomerProfileData
export type CustomerBrandingData = {
  firstName: string
  lastName: string
  photoUri: string
  email?: string
  phone?: string
  appointmentLinkUri?: string
}

/**
 * @returns Returns true or false depending on if we should show
 * the preferred agent or lo branding
 */
export const usePreferredAgentBranding = () => {
  const hasHomebotNetworkAccess: boolean = useSelector(selectHomebotNetworkEnabled)

  return hasHomebotNetworkAccess
}

const usePreferredAgent = (type?: PartnerType) => {
  const preferredRealEstateAgentMembership = useSelector(selectPreferredRealEstateAgentMembership)
  const preferredLoanOfficerMembership = useSelector(selectPreferredLoanOfficerMembership)
  const preferredInsuranceAgentMembership = useSelector(selectPreferredInsuranceAgentMembership)
  if (
    type == 'RealEstateAgent' &&
    preferredRealEstateAgentMembership &&
    preferredRealEstateAgentMembership.isVerified
  ) {
    return preferredRealEstateAgentMembership.customerProfile
  } else if (type == 'LoanOfficer' && preferredLoanOfficerMembership && preferredLoanOfficerMembership.isVerified) {
    return preferredLoanOfficerMembership.customerProfile
  } else if (
    type == 'InsuranceAgent' &&
    preferredInsuranceAgentMembership &&
    preferredInsuranceAgentMembership.isVerified
  ) {
    return preferredInsuranceAgentMembership.customerProfile
  }
  return null
}

/**
 * Checks if we should show the preferred agent. If so, return the branding object
 * constructed from the preferred agent or the preferred LO.
 *
 * If not, check if we should show the co-sponsor. If so, return the branding
 * object constructed from the co-sponsor.
 *
 * If neither or true, return the branding object from the customer profile.
 *
 * I wrote this while listening to the soundtrack from the movie "Armageddon" and hoping
 * that it attracts a large meteorite to the earth.
 */
export const useCustomerBranding = (type: PartnerType): CustomerBrandingData | null => {
  const customerProfile = useSelector(selectCustomerProfile)
  const showPreferredAgentBranding = usePreferredAgentBranding()
  const preferredAgent = usePreferredAgent(type)

  if (showPreferredAgentBranding && preferredAgent) {
    const preferredAgentBrandingData = mapClientTeamMembershipCustomerProfileToPartnerBrandingData(preferredAgent)
    return preferredAgentBrandingData
  }

  const customerBrandingData = mapCustomerProfileToCustomerBrandingData(customerProfile)
  return customerBrandingData
}

const mapClientTeamMembershipCustomerProfileToPartnerBrandingData = (
  customerProfile: ClientTeamMembershipCustomerProfileData
): CustomerBrandingData => {
  if (!customerProfile) {
    return null
  }
  return {
    firstName: customerProfile.firstName,
    lastName: customerProfile.lastName,
    photoUri: customerProfile.photoUri,
    phone: customerProfile.phone,
    email: customerProfile.email,
    appointmentLinkUri: customerProfile.appointmentLinkUri
  }
}

// mobilePhone gets mapped to phone, all other properties stay the same
const mapCustomerProfileToCustomerBrandingData = (customerProfile: CustomerProfileData): CustomerBrandingData => {
  if (!customerProfile) {
    return null
  }
  return {
    firstName: customerProfile.firstName,
    lastName: customerProfile.lastName,
    photoUri: customerProfile.photoUri,
    phone: customerProfile.mobilePhone,
    appointmentLinkUri: customerProfile.appointmentLinkUri,
    email: customerProfile.email
  }
}
